/**
 * Detailed Plugin View Component
 * 
 * Shows comprehensive plugin governance information:
 * - Permissions and scopes
 * - Security assessment
 * - Version history with rollback
 * - Installation metadata
 */

import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Descriptions,
  Tag,
  Table,
  Button,
  Alert,
  Space,
  Typography,
  Badge,
  Divider,
  Popconfirm,
  message,
  Tabs,
  Card,
  List,
  Tooltip
} from 'antd';
import {
  SafetyOutlined,
  KeyOutlined,
  HistoryOutlined,
  RollbackOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  ReloadOutlined
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

interface PluginPermission {
  method: string;
  path: string;
}

interface PluginVersion {
  version: string;
  installed_at: string;
  installed_by: string;
  current: boolean;
  changelog?: string;
}

interface PluginGovernance {
  plugin_id: string;
  enabled: boolean;
  quarantined: boolean;
  permissions: {
    api: PluginPermission[];
    sse: Array<{ prefix: string }>;
    total_api_permissions: number;
  };
  scopes: string[];
  security: {
    risk_level: 'low' | 'medium' | 'high';
    high_risk_operations: number;
    integrity?: string;
    bundle_hash?: string;
    last_modified?: number;
  };
  version: string;
  source: string;
  installed_at?: string;
  installed_by?: string;
  metadata: Record<string, any>;
}

interface DetailedPluginViewProps {
  pluginId: string | null;
  visible: boolean;
  onClose: () => void;
}

interface PluginManifest {
  id: string;
  name?: string;
  title?: string;
  version: string;
  description?: string;
  author?: string;
  icon?: string;
  enabled?: boolean;
  group?: string;
  permissions?: {
    api?: Array<{ method: string; path: string }>;
  };
}

const DetailedPluginView: React.FC<DetailedPluginViewProps> = ({
  pluginId,
  visible,
  onClose
}) => {
  const [loading, setLoading] = useState(false);
  const [governance, setGovernance] = useState<PluginGovernance | null>(null);
  const [versions, setVersions] = useState<PluginVersion[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [manifest, setManifest] = useState<PluginManifest | null>(null);
  const [governanceError, setGovernanceError] = useState<string | null>(null);
  const [versionsError, setVersionsError] = useState<string | null>(null);

  useEffect(() => {
    if (visible && pluginId) {
      // Reset states when opening
      setGovernance(null);
      setVersions([]);
      setManifest(null);
      setGovernanceError(null);
      setVersionsError(null);
      
      // Fetch manifest first (lightweight, usually available)
      fetchManifest();
      // Then fetch detailed data
      fetchGovernance();
      fetchVersions();
    }
  }, [visible, pluginId]);

  const fetchManifest = async () => {
    if (!pluginId) return;
    
    try {
      const res = await fetch('/api/plugins/manifests', { credentials: 'include' });
      if (!res.ok) return;
      
      const data = await res.json();
      const manifests = Array.isArray(data?.manifests) ? data.manifests : [];
      const found = manifests.find((m: any) => m.id === pluginId);
      if (found) {
        setManifest(found);
      }
    } catch (error) {
      // Silently fail - manifest is optional
      console.warn('Failed to fetch manifest:', error);
    }
  };

  const fetchGovernance = async () => {
    if (!pluginId) return;
    
    setLoading(true);
    setGovernanceError(null);
    try {
      const res = await fetch(`/api/plugins/${pluginId}/governance`, {
        credentials: 'include'
      });
      if (!res.ok) {
        if (res.status === 404) {
          setGovernanceError('Governance data not available for this plugin');
        } else {
          setGovernanceError(`Failed to load: ${res.status}`);
        }
        return;
      }
      
      const data = await res.json();
      setGovernance(data);
    } catch (error: any) {
      setGovernanceError(error.message || 'Failed to load governance data');
    } finally {
      setLoading(false);
    }
  };

  const fetchVersions = async () => {
    if (!pluginId) return;
    
    setLoadingVersions(true);
    setVersionsError(null);
    try {
      const res = await fetch(`/api/plugins/${pluginId}/versions`, {
        credentials: 'include'
      });
      if (!res.ok) {
        if (res.status === 404) {
          setVersionsError('Version history not available for this plugin');
        } else {
          setVersionsError(`Failed to load: ${res.status}`);
        }
        return;
      }
      
      const data = await res.json();
      setVersions(data.versions || []);
    } catch (error: any) {
      setVersionsError(error.message || 'Failed to load version history');
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleRollback = async (version: string) => {
    if (!pluginId) return;
    
    try {
      const res = await fetch(`/api/plugins/${pluginId}/rollback/${version}`, {
        method: 'POST',
        credentials: 'include'
      });
      if (!res.ok) throw new Error(`Rollback failed: ${res.status}`);
      
      const data = await res.json();
      message.success(data.message || `Rolled back to version ${version}`);
      await fetchVersions();
      await fetchGovernance();
    } catch (error: any) {
      message.error(`Rollback failed: ${error.message}`);
    }
  };

  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'high': return 'red';
      case 'medium': return 'orange';
      case 'low': return 'green';
      default: return 'default';
    }
  };

  const getRiskIcon = (riskLevel: string) => {
    switch (riskLevel) {
      case 'high': return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'medium': return <WarningOutlined style={{ color: '#fa8c16' }} />;
      case 'low': return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      default: return <InfoCircleOutlined />;
    }
  };

  const permissionColumns = [
    {
      title: 'Method',
      dataIndex: 'method',
      key: 'method',
      width: 100,
      render: (method: string) => {
        const colorMap: Record<string, string> = {
          'GET': 'blue',
          'POST': 'orange',
          'PUT': 'purple',
          'PATCH': 'cyan',
          'DELETE': 'red'
        };
        return <Tag color={colorMap[method] || 'default'}>{method}</Tag>;
      }
    },
    {
      title: 'Path',
      dataIndex: 'path',
      key: 'path'
    }
  ];

  // Show basic info from manifest if governance is not available
  const displayName = manifest?.name || manifest?.title || pluginId || 'Plugin';
  const displayVersion = manifest?.version || governance?.version || 'Unknown';
  const displayEnabled = manifest?.enabled !== false && (governance?.enabled ?? true);

  return (
    <Drawer
      title={
        <Space>
          <span>{displayName}</span>
          {displayEnabled ? (
            <Tag color="success">Enabled</Tag>
          ) : (
            <Tag>Disabled</Tag>
          )}
          {governance?.quarantined && (
            <Tag color="error">Quarantined</Tag>
          )}
        </Space>
      }
      placement="right"
      width={900}
      open={visible}
      onClose={onClose}
      loading={loading && !governance && !governanceError}
    >
      {governanceError && (
        <Alert
          message="Limited Information Available"
          description={
            <div>
              <Paragraph style={{ marginBottom: 8 }}>
                {governanceError}. Showing basic plugin information from manifest.
              </Paragraph>
              {manifest && (
                <Button 
                  size="small" 
                  onClick={fetchGovernance}
                  icon={<ReloadOutlined />}
                >
                  Retry
                </Button>
              )}
            </div>
          }
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Tabs defaultActiveKey="overview">
        <TabPane tab={<span><InfoCircleOutlined />Overview</span>} key="overview">
          <Card title="Plugin Information" style={{ marginBottom: 16 }}>
            <Descriptions column={2} bordered>
              <Descriptions.Item label="Plugin ID">
                {pluginId}
              </Descriptions.Item>
              <Descriptions.Item label="Name">
                {displayName}
              </Descriptions.Item>
              <Descriptions.Item label="Version">
                {displayVersion}
              </Descriptions.Item>
              {manifest?.author && (
                <Descriptions.Item label="Author">
                  {manifest.author}
                </Descriptions.Item>
              )}
              {governance?.source && (
                <Descriptions.Item label="Source">
                  <Tag>{governance.source}</Tag>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Status">
                <Space>
                  {displayEnabled ? (
                    <Badge status="success" text="Enabled" />
                  ) : (
                    <Badge status="default" text="Disabled" />
                  )}
                  {governance?.quarantined && (
                    <Badge status="error" text="Quarantined" />
                  )}
                </Space>
              </Descriptions.Item>
              {manifest?.group && (
                <Descriptions.Item label="Category">
                  <Tag>{manifest.group}</Tag>
                </Descriptions.Item>
              )}
              {manifest?.description && (
                <Descriptions.Item label="Description" span={2}>
                  {manifest.description}
                </Descriptions.Item>
              )}
              {governance?.installed_at && (
                <Descriptions.Item label="Installed At">
                  {new Date(governance.installed_at).toLocaleString()}
                </Descriptions.Item>
              )}
              {governance?.installed_by && (
                <Descriptions.Item label="Installed By">
                  {governance.installed_by}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          {governance?.security ? (
            <Card title="Security Assessment" style={{ marginBottom: 16 }}>
              <Alert
                message={
                  <Space>
                    {getRiskIcon(governance.security.risk_level)}
                    <span>
                      Risk Level: <Text strong>{governance.security.risk_level.toUpperCase()}</Text>
                    </span>
                  </Space>
                }
                description={
                  <div>
                    <Paragraph>
                      This plugin has <Text strong>{governance.security.high_risk_operations}</Text> high-risk operations
                      (POST, PUT, PATCH, DELETE).
                    </Paragraph>
                    {governance.security.bundle_hash && (
                      <Paragraph style={{ marginBottom: 0 }}>
                        <Text type="secondary">Bundle Hash: </Text>
                        <Text code style={{ fontSize: 11 }}>
                          {governance.security.bundle_hash.substring(0, 16)}...
                        </Text>
                      </Paragraph>
                    )}
                  </div>
                }
                type={governance.security.risk_level === 'high' ? 'error' : 
                      governance.security.risk_level === 'medium' ? 'warning' : 'success'}
                showIcon={false}
              />
            </Card>
          ) : (
            <Card title="Security Assessment" style={{ marginBottom: 16 }}>
              <Alert
                message="Security assessment not available"
                description="Detailed security information requires governance data, which is not available for this plugin."
                type="info"
                showIcon
              />
            </Card>
          )}

          {governance?.scopes && (
            <Card title="Scopes" style={{ marginBottom: 16 }}>
              {governance.scopes.length > 0 ? (
                <Space wrap>
                  {governance.scopes.map((scope, idx) => (
                    <Tag key={idx} color="blue">
                      <KeyOutlined /> {scope}
                    </Tag>
                  ))}
                </Space>
              ) : (
                <Text type="secondary">No scopes defined</Text>
              )}
            </Card>
          )}
        </TabPane>

        <TabPane tab={<span><SafetyOutlined />Permissions</span>} key="permissions">
          {governance?.permissions ? (
            <>
              <Card 
                title={`API Permissions (${governance.permissions.total_api_permissions})`}
                style={{ marginBottom: 16 }}
              >
                <Table
                  columns={permissionColumns}
                  dataSource={governance.permissions.api.map((p, idx) => ({
                    key: idx,
                    ...p
                  }))}
                  pagination={{ pageSize: 10 }}
                  size="small"
                />
              </Card>

              {governance.permissions.sse && governance.permissions.sse.length > 0 && (
                <Card title={`SSE Permissions (${governance.permissions.sse.length})`}>
                  <List
                    dataSource={governance.permissions.sse}
                    renderItem={(item, idx) => (
                      <List.Item>
                        <Text code>{item.prefix || JSON.stringify(item)}</Text>
                      </List.Item>
                    )}
                  />
                </Card>
              )}
            </>
          ) : manifest?.permissions?.api ? (
            <Card 
              title={`API Permissions (${manifest.permissions.api.length})`}
              style={{ marginBottom: 16 }}
            >
              <Alert
                message="Basic permission information"
                description="Showing permissions from plugin manifest. Detailed governance data is not available."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              <Table
                columns={permissionColumns}
                dataSource={manifest.permissions.api.map((p, idx) => ({
                  key: idx,
                  ...p
                }))}
                pagination={{ pageSize: 10 }}
                size="small"
              />
            </Card>
          ) : (
            <Card>
              <Alert
                message="Permissions not available"
                description="Permission information requires governance data, which is not available for this plugin."
                type="info"
                showIcon
              />
            </Card>
          )}
        </TabPane>

        <TabPane tab={<span><HistoryOutlined />Versions</span>} key="versions">
          <Card title="Version History">
            {versionsError ? (
              <Alert
                message="Version history not available"
                description={
                  <div>
                    <Paragraph style={{ marginBottom: 8 }}>
                      {versionsError}. Version history requires additional plugin metadata.
                    </Paragraph>
                    <Button 
                      size="small" 
                      onClick={fetchVersions}
                      icon={<ReloadOutlined />}
                    >
                      Retry
                    </Button>
                  </div>
                }
                type="warning"
                showIcon
              />
            ) : (
              <List
                loading={loadingVersions}
                dataSource={versions.length > 0 ? versions : undefined}
                locale={{ emptyText: 'No version history available' }}
                renderItem={(version) => (
                <List.Item
                  actions={[
                    version.current ? (
                      <Tag color="success">Current</Tag>
                    ) : (
                      <Popconfirm
                        title={`Rollback to version ${version.version}?`}
                        description="This will disable the current version and activate the selected version."
                        onConfirm={() => handleRollback(version.version)}
                        okText="Yes, Rollback"
                        cancelText="Cancel"
                      >
                        <Button 
                          size="small" 
                          icon={<RollbackOutlined />}
                          type="link"
                        >
                          Rollback
                        </Button>
                      </Popconfirm>
                    )
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Space>
                        <Text strong>Version {version.version}</Text>
                        {version.current && (
                          <Badge status="processing" text="Active" />
                        )}
                      </Space>
                    }
                    description={
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Installed: {new Date(version.installed_at).toLocaleString()} by {version.installed_by}
                        </Text>
                        {version.changelog && (
                          <div style={{ marginTop: 4 }}>
                            <Text style={{ fontSize: 12 }}>{version.changelog}</Text>
                          </div>
                        )}
                      </div>
                    }
                  />
                </List.Item>
                )}
              />
            )}
          </Card>
        </TabPane>
      </Tabs>
    </Drawer>
  );
};

export default DetailedPluginView;

